/**
 * What we sell, and how we talk about it.
 *
 * Copy and pricing live here rather than inline in the page so the whole
 * pitch can be rewritten without touching layout. Voice follows the brand
 * guide: short, playful, never corporate — a headline is a sentence, not a
 * value proposition.
 *
 * Prices are in cents, and are the *display* price only. The real price of
 * every plan comes from Stripe (`GET /api/premium/price`) and overrides the
 * number here, so the page can never quote something different from what the
 * card is charged. A plan Stripe has no Price for is shown but cannot be
 * bought, rather than quietly charging the wrong tier.
 */

/** The one subscription that exists today. */
export const PREMIUM = Object.freeze({
    id: 'premium',
    name: 'Gamebot Premium',
    tagline: 'Level faster. Look better.',
    priceCents: 499,
    interval: 'month',
    // `visual` picks which animated demo PremiumShowcase draws beside the
    // feature. It's a stable key rather than a title match so the copy stays
    // free to change without silently dropping a graphic.
    // Order is the pitch, and it is read in order three times over: the
    // showcase draws one act per feature, the plan columns list them as the
    // checklist, and both follow this array. Rewards lead — it is the
    // concrete number and the one a free player can see piling up unclaimed
    // on their own reward track.
    features: Object.freeze([
        {
            icon: 'award',
            visual: 'track',
            // Counted from LEVEL_REWARDS in server/services/rewards.js: 50
            // levels, 20 free rewards, 31 premium ones — and 30 of those
            // levels pay a free player nothing at all. The earlier line here
            // ("a second reward on every level") was simply untrue: exactly
            // one level carries both.
            //
            // Title is rounded down to "30+" on purpose — the table gets
            // retuned periodically (see the v3.8.0 track rebuild, which
            // moved this from 11/40 to 20/31), and a rounded claim survives
            // a few more retunings before it goes stale.
            title: '30+ more rewards a season',
            body: 'Every one of the 50 levels pays out — 51 rewards a season instead of 20.',
        },
        {
            icon: 'star',
            visual: 'xp',
            title: 'Double XP',
            body: 'Every game counts twice. Get through the season track in half the time.',
        },
        {
            icon: 'crown',
            visual: 'crown',
            title: 'A crown by your name',
            body: 'On your profile, next to everything you have earned.',
        },
        // Server Pass (leaderboards) used to be a fourth feature here. Pulled
        // for launch — the leaderboard it would demo only ranks globally
        // today, not per server, which was the entire pitch. See SERVER_PASS
        // below: the pass is still sold as a coming-soon add-on, just not
        // bundled into the base plan or shown as a working feature yet.
    ]),
})

/**
 * Server passes: leaderboards and competition for a whole server.
 *
 * Deliberately *not* content sharing. That was the original pitch, and it was
 * an unlimited-seat licence sold at a single-seat price — one pass in a large
 * server gave away the shop. Content sharing is now free and scoped to the
 * table instead (see GAM-66, content pooling), which leaves the pass free to
 * be the thing that genuinely gets better with more people: standings.
 *
 * Passes are **not sold on their own**. Every plan in PLANS is Premium plus a
 * number of servers, which keeps the offer to one axis — how many servers —
 * instead of a matrix of subscription combinations to explain, price, and
 * reconcile in the billing portal. `priceCents` is what a pass is *worth*: it
 * sets the list price the bulk tiers discount against. It is not a thing
 * anyone can buy by itself, which is why no copy quotes the figure — naming a
 * price for something with no buy button only invites the question of where
 * to buy it.
 */
export const SERVER_PASS = Object.freeze({
    id: 'server-pass',
    name: 'Server Pass',
    tagline: 'Settle it on the leaderboard.',
    priceCents: 299,
    interval: 'month',
    // Order is the pitch, and it is read in order three times over: the
    // showcase draws one act per feature, the plan columns list them as the
    // checklist, and both follow this array. Rewards lead — it is the
    // concrete number and the one a free player can see piling up unclaimed
    // on their own reward track.
    features: Object.freeze([
        {
            icon: 'chart-line',
            // The reason the pass exists. Flagged here rather than picked out
            // by position in the page, so reordering the list can't quietly
            // move the emphasis onto something else.
            highlight: true,
            title: 'Server leaderboards',
            body: 'Who actually wins the most, ranked. Bragging rights with receipts.',
        },
        {
            icon: 'trophy',
            title: 'A champion every season',
            body: 'Standings reset when the season does. Everyone starts level.',
        },
        {
            icon: 'gamepad',
            title: 'Every game on one board',
            body: 'Cards, Chess, Anagrams and the rest, all counting towards the same table.',
        },
    ]),
})

/**
 * The three tiers. Every plan is Gamebot Premium; the +3 and +10 tiers differ
 * only in how many servers get a Server Pass once that ships — one axis, three
 * points on it, so a visitor decides "how many servers do I run?" rather than
 * working out which combination of two subscriptions they want.
 *
 * Only the base plan is buyable today: the pass it would be paying for
 * (server leaderboards) isn't built yet, so `passComingSoon` forces the other
 * two tiers to render as not-yet-purchasable regardless of whether Stripe
 * happens to have a Price configured for them — see `plan.buyable` in
 * Premium.vue. Once the pass ships, drop the flag and they're for sale.
 *
 * The base plan's price is the one Stripe reports at runtime; the other two
 * are declared here and each needs its own Stripe Price before it can take
 * money (see `planPrices` on the profile store).
 *
 * Savings are derived, never typed — `planSaving()` measures against buying
 * Premium and then paying the full pass rate for each extra server, so the
 * badge cannot drift away from the price beside it.
 */
export const PLANS = Object.freeze([
    {
        id: 'premium',
        name: 'Gamebot Premium',
        // No pass bundled — see the note on PREMIUM.features above.
        servers: 0,
        priceCents: 499,
        tagline: 'For you and the server you play in.',
        // The marked column. It sat on premium-3 as "Most popular", which
        // claimed two things we can't stand behind right now: that we know
        // which plan is most popular, and that the plan is a live option —
        // its Server Pass is still coming soon, so its own button says so.
        // The base plan is the one anyone can actually buy today.
        //
        // Named `recommended`, not `highlight`: SERVER_PASS.features already
        // uses `highlight` for something unrelated.
        recommended: true,
    },
    {
        id: 'premium-3',
        name: 'Premium + 3 servers',
        servers: 3,
        priceCents: 899,
        tagline: 'For a few communities you actually run.',
        passComingSoon: true,
    },
    {
        id: 'premium-10',
        name: 'Premium + 10 servers',
        servers: 10,
        priceCents: 1999,
        tagline: 'For anyone running Gamebot at scale.',
        passComingSoon: true,
    },
])

/** The base plan — what `/api/premium/price` reports and what upsells quote. */
export const BASE_PLAN = PLANS[0]

/**
 * What a plan would cost à la carte: Premium once, plus the full pass rate for
 * every server beyond the first. The `- 1` is a pricing-model constant, not a
 * claim about what the base plan grants today — it's the reference point the
 * $8.99/$19.99 tiers were priced against, so the bulk discount curve stays
 * the one those numbers were tuned for even though the base plan itself
 * doesn't include a pass right now (see PLANS above).
 */
export function planListPrice(plan, premiumCents = BASE_PLAN.priceCents, passCents = SERVER_PASS.priceCents) {
    const extras = Math.max(0, (plan.servers || 0) - 1)
    return premiumCents + passCents * extras
}

/**
 * How much a plan saves against that list price, as a whole percentage. 0 when
 * it saves nothing, so the caller hides the badge rather than printing
 * "Save 0%" on the base plan.
 */
export function planSaving(plan, premiumCents, passCents) {
    const list = planListPrice(plan, premiumCents, passCents)
    if (!list || plan.priceCents >= list) return 0
    return Math.round(((list - plan.priceCents) / list) * 100)
}

/**
 * What each *extra* server costs on a plan — the number that actually falls as
 * the tiers grow. Dividing the whole price by the server count would flatter
 * the big tiers by folding Premium's own cost into the per-server rate.
 * Null on the base plan, which has no extras to price. Same `- 1` reference
 * point as planListPrice above.
 */
export function planExtraServerPrice(plan, premiumCents = BASE_PLAN.priceCents) {
    const extras = (plan.servers || 0) - 1
    if (extras <= 0) return null
    return Math.round((plan.priceCents - premiumCents) / extras)
}

/** `499` → `$4.99`. Whole amounts drop the cents: `$5`, not `$5.00`. */
export function formatPrice(cents, currency = 'usd') {
    const symbol = currency.toLowerCase() === 'usd' ? '$' : ''
    const amount = (cents || 0) / 100
    return `${symbol}${amount % 1 === 0 ? amount : amount.toFixed(2)}`
}

/**
 * What to tell a subscriber about their subscription.
 *
 * Four states, because they need different words and different buttons:
 *
 *  - `none`     — never subscribed, or cancelled and the period has run out.
 *  - `active`   — paid up, renews automatically.
 *  - `cancelling` — cancelled but still inside the paid period. They keep
 *                 everything until it ends, and can change their mind.
 *  - `lapsed`   — flagged premium but the period end has passed. Should be
 *                 unreachable: the subscription.deleted webhook clears the
 *                 flag. It's here because a webhook we never received would
 *                 otherwise show as a healthy subscription forever.
 */
export function subscriptionStatus(status, now = Date.now()) {
    const s = status || {}
    const until = s.premiumUntil || 0
    const ended = until > 0 && until <= now

    if (s.premium !== true) return { state: 'none' }
    if (ended) return { state: 'lapsed', until }
    if (s.cancelAtPeriodEnd === true) return { state: 'cancelling', until }
    return { state: 'active', until }
}

/** `1793491200000` → `19 Sep 2026`. Empty string when there's no date. */
export function formatRenewalDate(epochMs, locale = 'en-GB') {
    if (!epochMs) return ''
    return new Date(epochMs).toLocaleDateString(locale, {
        day: 'numeric', month: 'short', year: 'numeric',
    })
}

/**
 * Which of the three things a plan's button does.
 *
 * A function rather than a chain of `v-if`s because the *order* is the part
 * that was wrong: written as `isPremium` first, a subscriber saw "Manage
 * subscription" on a tier Stripe has no Price for — the Server Pass column,
 * which says "Coming soon" to everybody else. Availability is not a matter of
 * who is asking, so it is decided first.
 *
 * @param {{buyable: boolean}} plan A plan descriptor.
 * @param {boolean} isPremium Whether the viewer already subscribes.
 * @returns {'unavailable'|'manage'|'buy'}
 */
export function planAction(plan, isPremium) {
    // Nobody can buy a tier with no Price behind it, subscriber or not.
    if (!plan || !plan.buyable) return 'unavailable'
    // Already paying: the button is a signpost to the Customize tab, which is
    // the only route to Stripe's billing portal.
    if (isPremium) return 'manage'
    return 'buy'
}
